import { useState, useMemo, useEffect, useRef } from 'react';
import {
  ArrowLeft,
  Zap,
  Info,
  MapPin,
  Calendar,
  Building2,
  AlertTriangle,
  RotateCcw,
  TagIcon,
  Text,
  X,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useNavigate, useLocation } from 'react-router-dom';
import BottomNav from '../../components/layouts/Footer/BottomNav';

const ProductDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { product, subCategory } = location.state || {};
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const variantRefs = useRef([]);
  const thumbnailRefs = useRef([]);

  // --- Helper Functions (No Changes Here) ---
  const getAttributeIcon = (attributeName) => {
    const name = attributeName.toLowerCase();
    if (name.includes('description')) return <Text className="size-5" />;
    if (name.includes('tag')) return <TagIcon className="size-5" />;
    if (name.includes('disclaimer')) return <AlertTriangle className="size-5" />;
    if (name.includes('return') || name.includes('policy')) return <RotateCcw className="size-5" />;
    if (name.includes('shelf') || name.includes('life')) return <Calendar className="size-5" />;
    if (name.includes('country') || name.includes('origin')) return <MapPin className="size-5" />;
    if (name.includes('manufacturer')) return <Building2 className="size-5" />;
    if (name.includes('marketed')) return <Building2 className="size-5" />;
    return <Info className="w-5 h-5" />;
  };

  const openImageModal = () => {
    if (productImages.length > 0 && productImages[0] !== '/images/imagenotfound.webp') {
      setIsModalOpen(true);
    }
  };

  const closeImageModal = () => {
    setIsModalOpen(false);
  };

  const getAttributeGradient = (index) => {
    const gradients = [
      'from-blue-500 to-blue-600',
      'from-green-500 to-green-600',
      'from-purple-500 to-purple-600',
      'from-orange-500 to-orange-600',
      'from-pink-500 to-pink-600',
      'from-indigo-500 to-indigo-600',
      'from-teal-500 to-teal-600',
    ];
    return gradients[index % gradients.length];
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  // --- Helper Functions for Image Mapping ---
  const extractVariantFromImageUrl = (imageUrl) => {
    try {
      // Extract the part between last %2F and the file extension
      const parts = imageUrl.split('%2F');
      const lastPart = parts[parts.length - 1];
      const variantName = lastPart.split('.')[0];
      return variantName.toLowerCase().trim();
    } catch {
      return '';
    }
  };

  const findMatchingImageUrl = (variantName) => {
    if (!variantName || !product?.productImageUrls) {
      const images = Array.isArray(product?.productImageUrls)
        ? product.productImageUrls
        : [product?.productImageUrls || '/images/imagenotfound.webp'];
      return images[0] || '/images/imagenotfound.webp';
    }

    const normalizedVariant = variantName.toLowerCase().trim();
    const images = Array.isArray(product.productImageUrls)
      ? product.productImageUrls
      : [product.productImageUrls];

    const matchingImage = images.find((imageUrl) => {
      const imageVariant = extractVariantFromImageUrl(imageUrl);
      return imageVariant === normalizedVariant;
    });

    // Return matching image, or first image if no match
    return matchingImage || images[0] || '/images/imagenotfound.webp';
  };

  // Reverse mapping: Find variant that matches an image URL
  const findVariantForImage = (imageUrl) => {
    if (!imageUrl || !availableVariants.length) return null;

    const imageVariantName = extractVariantFromImageUrl(imageUrl);
    if (!imageVariantName) return null;

    const matchingVariantIndex = availableVariants.findIndex((variant) => {
      const variantName = variant.variant?.toLowerCase().trim();
      return variantName === imageVariantName;
    });

    return matchingVariantIndex !== -1 ? matchingVariantIndex : null;
  };

  // --- Memos & Effects (No Changes Here) ---
  const availableVariants = useMemo(() => {
    return product?.productVariants?.filter((variant) => variant.available) || [];
  }, [product?.productVariants]);

  const variantsWithUnitPrice = useMemo(() => {
    return availableVariants.map((variant, index) => {
      const discount =
        variant.mrp > variant.netPrice
          ? (((variant.mrp - variant.netPrice) / variant.mrp) * 100).toFixed(0)
          : 0;

      return {
        ...variant,
        index,
        discount,
        savings: variant.mrp - variant.netPrice,
      };
    });
  }, [availableVariants]);

  const selectedVariant = availableVariants[selectedVariantIndex];
  const savings = useMemo(() => {
    if (!selectedVariant || selectedVariant.mrp <= selectedVariant.netPrice) return null;
    const amount = selectedVariant.mrp - selectedVariant.netPrice;
    const percentage = ((amount / selectedVariant.mrp) * 100).toFixed(0);
    return { amount, percentage };
  }, [selectedVariant]);

  const productImages = useMemo(() => {
    if (!product?.productImageUrls || product.productImageUrls.length === 0) {
      return ['/images/imagenotfound.webp'];
    }
    return Array.isArray(product.productImageUrls)
      ? product.productImageUrls
      : [product.productImageUrls];
  }, [product?.productImageUrls]);

  // Get the image that matches the selected variant
  const displayImage = useMemo(() => {
    if (!selectedVariant?.variant) {
      return productImages[0] || '/images/imagenotfound.webp';
    }
    // const matchedImage = findMatchingImageUrl(selectedVariant.variant);
    // return matchedImage || productImages[0] || '/images/imagenotfound.webp';

    return productImages[selectedImageIndex] || '/images/imagenotfound.webp';
  }, [productImages, selectedImageIndex]);

  const selectedDiscount = useMemo(() => {
    if (!selectedVariant) return 0;
    const mrp = selectedVariant.mrp;
    const netPrice = selectedVariant.netPrice;
    return mrp && netPrice && mrp > netPrice ? (((mrp - netPrice) / mrp) * 100).toFixed(0) : 0;
  }, [selectedVariant]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-scroll thumbnail into view when image changes
  useEffect(() => {
    if (thumbnailRefs.current[selectedImageIndex]) {
      thumbnailRefs.current[selectedImageIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [selectedImageIndex]);

  // Update selected image when variant changes
  useEffect(() => {
    if (selectedVariant?.variant) {
      const matchedImage = findMatchingImageUrl(selectedVariant.variant);
      const matchedIndex = productImages.findIndex((img) => img === matchedImage);
      if (matchedIndex !== -1 && matchedIndex !== selectedImageIndex) {
        setSelectedImageIndex(matchedIndex);
      }
    }
  }, [selectedVariant]);

  // --- Product Not Found Fallback (No Changes Here) ---
  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🛒</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Product not found</h2>
          <p className="text-gray-600 mb-4">
            The product you&apos;re looking for doesn&apos;t exist.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-[#F77C06] text-white px-6 py-2 rounded-lg hover:bg-[#e66a05] transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const firstVariant = availableVariants?.length > 0 ? availableVariants[0] : null;

  // --- RESPONSIVE JSX STRUCTURE ---
  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Header with Breadcrumbs */}
      <div className="bg-white border-b sticky top-16 z-10">
        <div className="mx-auto px-4 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={handleGoBack}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center text-sm text-gray-600 overflow-hidden">
              <span className="hover:text-[#F77C06] cursor-pointer" onClick={() => navigate('/')}>
                Home
              </span>
              <span className="mx-2">/</span>
              <span
                className="hover:text-[#F77C06] cursor-pointer whitespace-nowrap"
                onClick={() => navigate(-1)}
              >
                {subCategory?.name}
              </span>
              <span className="mx-2">/</span>
              <span className="text-gray-900 truncate">{product.productName}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid - REORDERED for image on left */}
      <div className=" mx-auto pb-20 md:pb-3 max-w-8xl px-4 py-3">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-12">
          {/* Left Column: Product Images (Sticky on Large Screens) */}
          <div className="lg:sticky lg:top-24 lg:col-span-1 self-start space-y-2">
            <div
              onClick={openImageModal}
              className="h-[35vh] md:min-h-[60vh] py-3 px-4 rounded-2xl border border-gray-200 relative flex items-center justify-center overflow-hidden"
            >
              <img
                src={displayImage}
                alt={product.productName}
                className="max-w-full max-h-full object-contain rounded-md transition-opacity duration-300"
                key={displayImage}
                onError={(e) => {
                  e.target.src = '/images/imagenotfound.webp';
                }}
              />
              {savings && (
                <div className="absolute -top-2 left-0 bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm px-3 py-1.5 rounded-full font-bold shadow-lg flex items-center gap-1">
                  <Zap className="w-4 h-4" />
                  SAVE {savings.percentage}%
                </div>
              )}
            </div>
            {/* Thumbnail Images below main image */}
            {productImages.length > 1 && (
              <div className="relative mt-5">
                <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 scrollbar-hide">
                  {productImages.map((image, index) => (
                    <button
                      key={index}
                      ref={(el) => (thumbnailRefs.current[index] = el)}
                      onClick={() => {
                        setSelectedImageIndex(index);
                        // Find and select the matching variant
                        const matchingVariantIndex = findVariantForImage(image);
                        if (matchingVariantIndex !== null) {
                          setSelectedVariantIndex(matchingVariantIndex);
                        }
                      }}
                      className={`flex-shrink-0 w-20 h-20 snap-center bg-white rounded-lg border-2 p-1 overflow-hidden transition-all transform ${
                        selectedImageIndex === index
                          ? ' border-[#F77C06]'
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <img
                        src={image || '/images/imagenotfound.webp'}
                        alt={`${product.productName} thumbnail ${index + 1}`}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.src = '/images/imagenotfound.webp';
                        }}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Product Details & Actions */}
          <div className="space-y-4 lg:col-span-2">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">
              {product.productName}
            </h1>

            {/* Price & Add to Cart Section */}
            <div className="flex flex-row items-start justify-between gap-2">
              <div className="space-y-1 flex-grow">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-gray-900">
                    ₹{selectedVariant?.netPrice}
                  </span>
                  {selectedVariant?.mrp > selectedVariant?.netPrice && (
                    <span className="text-lg text-gray-500 line-through">
                      MRP ₹{selectedVariant?.mrp}
                    </span>
                  )}
                </div>
                {selectedVariant?.mrp > selectedVariant?.netPrice && (
                  <p className="text-sm font-semibold text-green-600">
                    You save ₹{selectedVariant?.mrp - selectedVariant?.netPrice} ({selectedDiscount}
                    % OFF)
                  </p>
                )}
                <p className="text-xs text-gray-500">(Inclusive of all taxes)</p>
              </div>
            </div>

            {/* Variant Selection (Unchanged, already fits theme) */}
            {availableVariants?.length > 1 ? (
              <div className="space-y-2">
                <div className="flex gap-1 pt-4 overflow-y-visible overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 scrollbar-hide">
                  {variantsWithUnitPrice.map((variant, index) => {
                    const isSelected = selectedVariantIndex === index;
                    return (
                      <button
                        key={variant.variantId}
                        ref={(el) => (variantRefs.current[index] = el)}
                        onClick={() => setSelectedVariantIndex(index)}
                        className={`relative overflow-y-visible snap-center pt-3 pb-1.5 px-3 rounded-md border transition-all transform ${
                          isSelected
                            ? 'border-[#F77C06] bg-orange-50 shadow-md'
                            : 'border-gray-200 bg-white hover:border-gray-400'
                        }`}
                      >
                        <div className="text-center space-y-1 min-w-[30px]">
                          <div className="font-bold text-sm text-gray-900">
                            {variant.variant} {variant.unit}
                          </div>
                          <div className="flex items-center justify-center gap-2">
                            <span className="text-sm font-bold text-green-600">
                              ₹{variant.netPrice}
                            </span>
                            {variant.mrp > variant.netPrice && (
                              <span className="text-sm text-gray-500 line-through">
                                ₹{variant.mrp}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="absolute w-full -top-3 left-0 flex items-center justify-center">
                          {Number(variant.discount) > 0 && (
                            <div
                              className={` text-white w-fit text-xs px-2 py-1 rounded-full font-bold shadow-lg ${
                                Number(variant.discount) >= 20 ? 'bg-red-500' : 'bg-orange-500'
                              }`}
                            >
                              {variant.discount}% OFF
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-left">
                <div className="font-medium text-base text-gray-900">
                  {firstVariant?.variant} {firstVariant?.unit}
                </div>
              </div>
            )}

            {product?.description && (
              <div>
                <h4 className="px-3 mb-3 font-semibold text-lg">Overview</h4>
                <div
                  className="bg-gray-50 rounded-md p-3 text-gray-700 text-sm
                                  [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-3 [&_h1]:text-gray-900
                                  [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-2 [&_h2]:text-gray-900
                                  [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mb-2 [&_h3]:text-gray-900
                                  [&_p]:mb-3 [&_p]:leading-relaxed
                                  [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mb-3 [&_ul]:space-y-1x
                                  [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:mb-3 [&_ol]:space-y-1
                                  [&_li]:leading-relaxed
                                  [&_strong]:font-semibold [&_strong]:text-gray-900
                                  [&_em]:italic
                                  [&_a]:text-[#F77C06] [&_a]:underline [&_a]:hover:text-orange-600"
                  dangerouslySetInnerHTML={{
                    __html: product?.description,
                  }}
                />
              </div>
            )}

            {/* --- MODIFIED: Product Details Accordion (Sleek Version) --- */}
            {(product?.brand || product?.productAttributes?.length > 0) && (
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="product-details">
                  <AccordionTrigger className="hover:no-underline px-3 py-2 transition-colors group w-full">
                    <div className="text-gray-800 px-2 py-1">
                      <h3 className="font-bold text-lg flex items-center gap-2">
                        <Info className="w-5 h-5 text-[#F77C06]" />
                        Product Details
                      </h3>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2 pb-0 px-0">
                    <div className="p-0">
                      <Accordion type="single" collapsible className="w-full">
                        {product?.brand && (
                          <AccordionItem value="brand" className="border-t border-gray-200">
                            <AccordionTrigger className="hover:no-underline px-4 py-3 transition-colors group w-full text-left">
                              <div className="flex items-center gap-3">
                                <div
                                  className={`w-10 h-10 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-105 transition-transform`}
                                >
                                  <div className="text-white font-bold">
                                    {product?.brand?.charAt(0)}
                                  </div>
                                </div>
                                <div>
                                  <h4 className="font-semibold text-gray-900 text-base">Brand</h4>
                                </div>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="pt-0 pb-4 pr-4">
                              <div className="bg-gray-50 rounded-md p-3">
                                <p className="text-gray-700 text-sm">{product?.brand}</p>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        )}
                        {product?.productAttributes?.map((attribute, index) => (
                          <AccordionItem
                            key={index}
                            value={`item-${index}`}
                            className="border-t border-gray-200"
                          >
                            <AccordionTrigger className="hover:no-underline px-4 py-3 transition-colors group w-full text-left">
                              <div className="flex items-center gap-3">
                                <div
                                  className={`w-10 h-10 bg-gradient-to-r ${getAttributeGradient(
                                    index
                                  )} rounded-full flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-105 transition-transform`}
                                >
                                  <div className="text-white">
                                    {getAttributeIcon(attribute.name)}
                                  </div>
                                </div>
                                <div>
                                  <h4 className="font-semibold text-gray-900 text-base">
                                    {attribute.name}
                                  </h4>
                                </div>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="pt-0 pb-4 pr-4">
                              <div className="bg-gray-50 rounded-md p-3">
                                <p className="text-gray-700 text-sm">{attribute.value}</p>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
            {/* --- END MODIFIED Accordion --- */}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 transition-opacity duration-300"
          onClick={closeImageModal} // Close on overlay click
        >
          <div
            className="relative max-w-8xl h-[90vh] max-[h-90vh]"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on the image
          >
            <img
              src={productImages[selectedImageIndex] || '/images/imagenotfound.webp'}
              alt={product.productName}
              className="h-full w-auto max-w-full max-h-[4Vhw] object-contain rounded-lg"
              onError={(e) => {
                e.target.src = '/images/imagenotfound.webp';
              }}
            />
            <button
              onClick={closeImageModal}
              className="absolute top-0 right-0 -mt-4 -mr-4 bg-white text-gray-800 rounded-full p-2 shadow-lg hover:bg-gray-200 transition-colors"
              aria-label="Close image viewer"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default ProductDetails;
